/**
 * The sprite's path data, in its own module.
 *
 * It lived inside `Icon.tsx`, which meant anything else needing the NAME LIST had to
 * duplicate it — and the first attempt at that did exactly what duplicated lists always do:
 * copied 13 of the 39 names, so two thirds of the sprite would have silently fallen back to
 * a default glyph. One list, imported by both the component and the narrowing helper.
 */
export const PATHS = {
  'ic-gauge': 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM13.4 10.6 19 5M3 12a9 9 0 0 1 18 0',
  'ic-layers': 'm12 2 9 5-9 5-9-5 9-5ZM3 12l9 5 9-5M3 17l9 5 9-5',
  'ic-tower': 'M8 21h8M12 21V8M6 3l12 12M18 3 6 15M9 8h6',
  'ic-plug': 'M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-12 0V8ZM12 17v5',
  'ic-shield': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  /* A padlock: what cannot be changed here (Access › Roles, a role that comes with the
     organisation). */
  'ic-lock': 'M6 11h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2ZM8 11V7a4 4 0 0 1 8 0v4',
  'ic-building': 'M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M8 7h1M8 11h1M8 15h1M12 7h1M12 11h1M12 15h1M16 21h4V11h-4M2 21h20',
  'ic-cable': 'M4 4v7a4 4 0 0 0 8 0V4M4 2h4M8 2v2M12 20v-7a4 4 0 0 1 8 0v7M16 22h4M20 22v-2',
  'ic-globe': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18Z',
  'ic-library': 'M4 3h4v18H4zM10 3h4v18h-4zM17 4l4 16-3 1-4-16z',
  'ic-check2': 'm4 13 4 4L20 5',
  'ic-calc': 'M5 2h14a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1ZM8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h8',
  'ic-sliders': 'M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M16 4v4M10 10v4M18 16v4',
  'ic-file': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6ZM14 2v6h6M9 14h6M9 18h6',
  'ic-sun': 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  'ic-moon': 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z',
  'ic-chevdown': 'm6 9 6 6 6-6',
  'ic-logout': 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  'ic-chevup': 'm18 15-6-6-6 6',
  'ic-chevright': 'm9 18 6-6-6-6',
  'ic-chevleft': 'm15 18-6-6 6-6',
  'ic-search': 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  /* Something worth a look on a thing that is otherwise fine (the Plants card, 2026-10-09). */
  'ic-eye': 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  /* Three dots, upright: the dock's Edit tile. Round caps (Icon's) make the zero-length strokes show. */
  'ic-more': 'M12 5h.01M12 12h.01M12 19h.01',
  'ic-edit': 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z',
  /* The discussion beside an alarm or a work order (0102): a file, a comment, a reply. */
  'ic-paperclip': 'm21.4 11.1-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5',
  'ic-message': 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z',
  'ic-reply': 'M9 17 4 12l5-5M20 18v-2a4 4 0 0 0-4-4H4',
  'ic-calendar': 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z',
  'ic-download': 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  'ic-x': 'M18 6 6 18M6 6l12 12',
  'ic-plus': 'M12 5v14M5 12h14',
  'ic-bell': 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  'ic-check': 'M20 6 9 17l-5-5',
  'ic-alert': 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  'ic-info': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16v-4M12 8h.01',
  'ic-minus': 'M5 12h14',
  'ic-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z',
  'ic-bolt': 'M13 2 3 14h8l-1 8 10-12h-8l1-8Z',
  'ic-print': 'M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z',
  'ic-activity': 'M22 12h-4l-3 9L9 3l-3 9H2',
  'ic-server': 'M4 4h16v6H4zM4 14h16v6H4zM8 7h.01M8 17h.01',
  'ic-battery': 'M2 7h16v10H2zM22 11v2M6 11v2M10 11v2',
  'ic-zap': 'M13 2 3 14h8l-1 8 10-12h-8l1-8Z',
  'ic-route': 'M6 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM15 5H9a4 4 0 0 0 0 8h6a4 4 0 0 1 0 8H9',
  /* Corner brackets pointing out / in. Reads as full-screen at 13px, where a four-arrow
     glyph turns to mush. */
  'ic-maximize': 'M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3',
  'ic-minimize': 'M3 8V5a2 2 0 0 1 2-2h3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M21 16v3a2 2 0 0 1-2 2h-3',
  'ic-target': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  /* ── the twin studio's editing verbs. Added with the plan editor: an editor with no
     undo glyph asks the operator to read four words where a shape would do. */
  'ic-undo': 'm9 14-5-5 5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
  'ic-redo': 'm15 14 5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13',
  'ic-trash': 'M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6',
  'ic-rotate': 'M21 12a9 9 0 1 1-2.6-6.4L21 8M21 3v5h-5',
  'ic-copy': 'M9 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1ZM5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1',
  'ic-cursor': 'M4 3l6.5 17 2.6-6.9L20 10.5 4 3Z',
  'ic-square': 'M4 4h16v16H4z',
  'ic-door': 'M3 21h18M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17M14 12h.01',
  'ic-save': 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2ZM17 21v-8H7v8M7 3v5h8',
  'ic-grid': 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18',
  'ic-move': 'M12 2v20M2 12h20M9 5l3-3 3 3M9 19l3 3 3-3M5 9l-3 3 3 3M19 9l3 3-3 3',
  /* The weather station's three readings beside `ic-sun` (2026-10-09): air, panels, wind. */
  'ic-thermometer': 'M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z',
  'ic-solar-panel': 'M4 4h16l2 10H2L4 4ZM3 9h18M9 4l-1 10M15 4l1 10M12 14v7M8 21h8',
  'ic-wind': 'M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2M9.6 4.6A2 2 0 1 1 11 8H2M12.6 19.4A2 2 0 1 0 14 16H2',
  /* The description editor's tools (2026-10-12): a bulleted list, a numbered list, a link. */
  'ic-list': 'M9 6h11M9 12h11M9 18h11M4.5 6h.01M4.5 12h.01M4.5 18h.01',
  'ic-list-ordered': 'M10 6h10M10 12h10M10 18h10M4 4h1v4M4 8h2M4 14.5a1 1 0 0 1 2 0c0 1-2 1.5-2 3h2',
  'ic-link': 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
} as const;

export type IconName = keyof typeof PATHS;
